import React, { useCallback, useEffect, useState } from 'react';
import { adminService, storeAppUrl } from '../../services/adminApi';
import { GlassButton, GlassCard, GlassPanel, LoadingSpinner } from '../../components/GlassUI';
import {
  FaDatabase, FaDownload, FaSyncAlt, FaTrash, FaCheck, FaTimes,
  FaExclamationTriangle, FaExternalLinkAlt, FaShieldAlt, FaLink,
} from 'react-icons/fa';

const fmtSize = (b: number) => {
  if (!b) return '0 B';
  if (b > 1024 * 1024) return (b / (1024 * 1024)).toFixed(1) + ' MB';
  if (b > 1024) return (b / 1024).toFixed(1) + ' KB';
  return b + ' B';
};

const AdminSystemPage: React.FC = () => {
  const [backups, setBackups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'backup' | 'reset' | null>(null);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await adminService.listBackups();
      setBackups(data.backups || []);
    } catch (err: any) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Failed to load backups' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const doBackup = async () => {
    setBusy('backup');
    setMsg(null);
    try {
      const res = await adminService.createBackup();
      setMsg({ type: 'ok', text: `${res.message} (${fmtSize(res.sizeBytes)})` });
      load();
    } catch (err: any) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Backup failed' });
    } finally {
      setBusy(null);
    }
  };

  const doReset = async () => {
    if (
      !window.confirm(
        'This removes ALL demo data (customers, orders, repairs, old gold). Your admin account and subscription stay. A backup is recommended first. Continue?'
      )
    ) {
      return;
    }
    setBusy('reset');
    setMsg(null);
    try {
      const res = await adminService.resetData();
      setMsg({ type: 'ok', text: res.message || 'Demo data cleared' });
    } catch (err: any) {
      setMsg({ type: 'err', text: err.response?.data?.error || 'Reset failed' });
    } finally {
      setBusy(null);
    }
  };

  const download = (file: string) => {
    // Backups live on the server; expose download through a signed URL later.
    // For now open the CRM system page where backups are managed manually.
    setMsg({ type: 'ok', text: `Backup "${file}" is stored on the server in backend/backups/.` });
  };

  return (
    <div className='space-y-5'>
      <div>
        <h1 className='text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3'>
          <span className='w-10 h-10 rounded-xl bg-slate-900 text-amber-400 flex items-center justify-center'>
            <FaDatabase />
          </span>
          Backup & System
        </h1>
        <p className='text-slate-500 mt-1 text-sm'>
          Secure your data, restore points & keep the store clean
        </p>
      </div>

      {msg && (
        <div
          className={`flex items-start gap-2 text-sm rounded-2xl px-4 py-3 border ${
            msg.type === 'ok'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {msg.type === 'ok' ? <FaCheck className='mt-0.5 shrink-0' /> : <FaExclamationTriangle className='mt-0.5 shrink-0' />}
          <span className='break-all'>{msg.text}</span>
          <button className='ml-auto text-xs' onClick={() => setMsg(null)}>
            <FaTimes />
          </button>
        </div>
      )}

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6'>
        {/* Backup */}
        <GlassPanel className='p-5 md:p-6 flex flex-col'>
          <div className='flex items-center gap-3 mb-2'>
            <div className='w-10 h-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center'>
              <FaDatabase />
            </div>
            <div>
              <h2 className='font-bold text-slate-900'>Database Backup</h2>
              <p className='text-xs text-slate-500'>Full snapshot of every record</p>
            </div>
          </div>
          <p className='text-sm text-slate-600 mb-4 flex-1'>
            Creates a complete copy of the database on the server. Take one before
            plan changes, big updates or cleanup. Your data is never lost.
          </p>
          <GlassButton variant='gold' onClick={doBackup} disabled={busy === 'backup'} className='w-full sm:w-auto'>
            <FaDownload /> {busy === 'backup' ? 'Backing up...' : 'Backup Database Now'}
          </GlassButton>
        </GlassPanel>

        {/* Reset */}
        <GlassPanel className='p-5 md:p-6 flex flex-col border-red-200'>
          <div className='flex items-center gap-3 mb-2'>
            <div className='w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center'>
              <FaTrash />
            </div>
            <div>
              <h2 className='font-bold text-slate-900'>Reset Demo Data</h2>
              <p className='text-xs text-slate-500'>Start the store fresh</p>
            </div>
          </div>
          <p className='text-sm text-slate-600 mb-4 flex-1'>
            Removes all customers, orders, repairs & old-gold records so you can start
            with a clean slate. Your admin account and plan remain untouched.
          </p>
          <GlassButton variant='danger' onClick={doReset} disabled={busy === 'reset'} className='w-full sm:w-auto'>
            <FaTrash /> {busy === 'reset' ? 'Resetting...' : 'Clear All Demo Data'}
          </GlassButton>
        </GlassPanel>
      </div>

      {/* Recent backups */}
      <div>
        <div className='flex items-center justify-between mb-3'>
          <h2 className='text-lg font-bold text-slate-900 flex items-center gap-2'>
            <FaSyncAlt className='text-amber-500' /> Backup History
          </h2>
          <button onClick={load} className='text-xs font-semibold text-slate-500 hover:text-slate-800 flex items-center gap-1'>
            <FaSyncAlt size={11} /> Refresh
          </button>
        </div>
        {loading ? (
          <LoadingSpinner label='Checking backups...' className='py-10' />
        ) : backups.length === 0 ? (
          <GlassCard className='text-center py-10 text-slate-400'>
            <FaDatabase className='text-3xl mx-auto mb-2 text-slate-300' />
            <p className='text-sm'>No backups yet — create your first one above.</p>
          </GlassCard>
        ) : (
          <div className='space-y-2'>
            {backups.map((b) => (
              <GlassPanel key={b.file} className='px-4 py-3 flex items-center gap-3'>
                <FaDatabase className='text-emerald-500 shrink-0' />
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-semibold text-slate-900 truncate'>{b.file}</p>
                  <p className='text-xs text-slate-500'>
                    {fmtSize(b.sizeBytes)} · {new Date(b.createdAt).toLocaleString()}
                  </p>
                </div>
                <GlassButton variant='secondary' className='!px-3 !py-1.5 text-xs' onClick={() => download(b.file)}>
                  <FaDownload size={11} /> Info
                </GlassButton>
              </GlassPanel>
            ))}
          </div>
        )}
      </div>

      {/* App links */}
      <GlassPanel className='p-5 md:p-6'>
        <h2 className='font-bold text-slate-900 mb-3 flex items-center gap-2'>
          <FaLink className='text-indigo-500' /> App URLs
        </h2>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm'>
          <a href={storeAppUrl()} className='flex items-center gap-2 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 hover:bg-slate-100 text-slate-700 font-medium transition-all'>
            <FaExternalLinkAlt className='text-amber-500 shrink-0' /> /crm — Jewelry Store CRM
          </a>
          <span className='flex items-center gap-2 px-4 py-3 rounded-xl bg-slate-900 text-white font-medium'>
            <FaShieldAlt className='text-amber-400 shrink-0' /> /admin — Admin Control (you are here)
          </span>
        </div>
        <p className='text-xs text-slate-400 mt-3'>
          Customer scan page: /crm/customer-form · Staff login: /crm/login
        </p>
      </GlassPanel>
    </div>
  );
};

export default AdminSystemPage;
